import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Search, CheckCircle, ChevronRight, Sparkles } from 'lucide-react';
import ResponsiveImage from './ResponsiveImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const formatDateDe = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

/**
 * Public landing-page teaser: lets visitors pick a date and immediately see
 * which products are still available — strong conversion driver since users
 * skip the catalogue browsing and go straight to a date-specific inquiry.
 */
const PublicAvailabilitySection = () => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateToCustom, setDateToCustom] = useState('');
  const [mode, setMode] = useState('1'); // '1' = 1 Tag, 'range' = eigener Zeitraum
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  // Categories for the optional filter
  useEffect(() => {
    axios.get(`${API}/categories`)
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  const addDaysIso = (iso, days) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${dt.getUTCFullYear()}-${mm}-${dd}`;
  };

  // Compute effective date range from mode
  const daysBetween = (fromIso, toIso) => {
    const [y1, m1, d1] = fromIso.split('-').map(Number);
    const [y2, m2, d2] = toIso.split('-').map(Number);
    const a = Date.UTC(y1, m1 - 1, d1);
    const b = Date.UTC(y2, m2 - 1, d2);
    return Math.max(1, Math.round((b - a) / 86400000) + 1);
  };
  const effectiveDateTo = mode === 'range' && dateToCustom ? dateToCustom : dateFrom;
  const effectiveDuration = mode === 'range' && dateToCustom ? daysBetween(dateFrom, dateToCustom) : 1;

  const check = async () => {
    if (!dateFrom) return;
    if (mode === 'range' && (!dateToCustom || dateToCustom < dateFrom)) return;
    setLoading(true);
    setTouched(true);
    try {
      const res = await axios.post(`${API}/availability/public-check`, {
        date_from: dateFrom,
        date_to: effectiveDateTo,
        category: category || undefined,
        limit: 200, // alle verfügbaren Produkte zeigen
      });
      setResult(res.data);
    } catch (e) {
      setResult({ available: [], available_count: 0, unavailable_count: 0, total: 0, error: true });
    } finally {
      setLoading(false);
    }
  };

  const minDate = useMemo(() => todayIso(), []);

  const goProduct = (p) => {
    // Open the product detail page directly (carry date + duration as context)
    const params = new URLSearchParams({
      date: dateFrom,
      duration: String(effectiveDuration),
    });
    navigate(`/produkte/${p.slug || p.id}?${params.toString()}`);
  };

  return (
    <section className="relative pt-2 pb-6 md:pt-3 md:pb-9" data-testid="public-availability-section">
      <div className="container max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-4 md:mb-5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400 text-sky-900 text-[10px] font-extrabold uppercase tracking-wider mb-2">
            <Sparkles className="h-3 w-3" />
            Sofort verfügbar
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-sky-700 leading-tight">
            Verfügbarkeit prüfen
          </h2>
          <p className="text-neutral-600 mt-1.5 max-w-lg mx-auto text-sm">
            Wähle dein Wunschdatum – wir zeigen sofort, was frei ist.
          </p>
        </div>

        {/* Picker */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 max-w-6xl mx-auto border border-sky-100">
          <div className={`grid grid-cols-1 gap-3 items-end ${mode === 'range'
            ? 'md:grid-cols-[1fr_1fr_1.1fr_1.1fr_auto]'
            : 'md:grid-cols-[1.3fr_1fr_1.2fr_auto]'}`}>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">
                {mode === 'range' ? 'Von' : 'Datum'}
              </label>
              <div className="relative">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-500 pointer-events-none" />
                <input
                  type="date"
                  value={dateFrom}
                  min={minDate}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full h-10 pl-9 pr-3 rounded-full border-2 border-neutral-200 text-sm font-medium focus:border-sky-500 focus:outline-none transition-colors"
                  data-testid="pub-avail-date"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">Mietdauer</label>
              <div className="grid grid-cols-2 gap-2" data-testid="pub-avail-duration">
                <button
                  type="button"
                  onClick={() => setMode('1')}
                  className={`h-10 rounded-full font-bold text-xs transition-all border-2 ${
                    mode === '1'
                      ? 'bg-sky-700 text-white border-sky-700 shadow'
                      : 'bg-white text-neutral-700 border-neutral-200 hover:border-sky-400'
                  }`}
                  data-testid="pub-avail-duration-1"
                >
                  1 Tag
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('range');
                    if (!dateToCustom) setDateToCustom(addDaysIso(dateFrom, 2));
                  }}
                  className={`h-10 rounded-full font-bold text-xs transition-all border-2 ${
                    mode === 'range'
                      ? 'bg-sky-700 text-white border-sky-700 shadow'
                      : 'bg-white text-neutral-700 border-neutral-200 hover:border-sky-400'
                  }`}
                  data-testid="pub-avail-duration-range"
                >
                  Zeitraum
                </button>
              </div>
            </div>
            {mode === 'range' && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">Bis</label>
                <div className="relative">
                  <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-500 pointer-events-none" />
                  <input
                    type="date"
                    value={dateToCustom}
                    min={dateFrom || minDate}
                    onChange={(e) => setDateToCustom(e.target.value)}
                    className="w-full h-10 pl-9 pr-3 rounded-full border-2 border-neutral-200 text-sm font-medium focus:border-sky-500 focus:outline-none transition-colors"
                    data-testid="pub-avail-date-to"
                  />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">Kategorie (optional)</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-10 px-3 rounded-full border-2 border-neutral-200 text-sm font-medium focus:border-sky-500 focus:outline-none bg-white"
                data-testid="pub-avail-category"
              >
                <option value="">Alle Kategorien</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={check}
              disabled={!dateFrom || loading || (mode === 'range' && (!dateToCustom || dateToCustom < dateFrom))}
              className="h-10 px-5 rounded-full bg-sky-700 hover:bg-sky-800 disabled:bg-neutral-300 text-white font-bold text-xs inline-flex items-center gap-1.5 justify-center transition-colors"
              data-testid="pub-avail-check"
            >
              <Search className="h-3.5 w-3.5" />
              {loading ? 'Lädt...' : 'Prüfen'}
            </button>
          </div>
          {mode === 'range' && dateFrom && dateToCustom && dateToCustom >= dateFrom && (
            <p className="text-[11px] text-sky-700 font-semibold mt-3" data-testid="pub-avail-range-hint">
              Zeitraum: {formatDateDe(dateFrom)} – {formatDateDe(dateToCustom)} ({effectiveDuration} {effectiveDuration === 1 ? 'Tag' : 'Tage'})
            </p>
          )}
        </div>

        {/* Result */}
        {touched && (
          <div className="mt-10 max-w-7xl mx-auto" data-testid="pub-avail-result">
            {result?.error ? (
              <p className="text-center text-rose-600 font-semibold">
                Verfügbarkeit konnte nicht geladen werden. Bitte später erneut versuchen.
              </p>
            ) : result && result.available_count > 0 ? (
              <>
                <div className="flex items-center justify-center gap-2 mb-6 text-base md:text-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-neutral-900 font-semibold" data-testid="pub-avail-count">
                    {result.available_count} {result.available_count === 1 ? 'Produkt' : 'Produkte'} verfügbar{' '}
                    {effectiveDuration > 1 ? (
                      <>vom <span className="text-sky-700 tabular-nums">{formatDateDe(result.date_from)}</span> bis <span className="text-sky-700 tabular-nums">{formatDateDe(result.date_to)}</span></>
                    ) : (
                      <>am <span className="text-sky-700 tabular-nums">{formatDateDe(result.date_from)}</span></>
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {result.available.map((p) => (
                    <article
                      key={p.id}
                      onClick={() => goProduct(p)}
                      className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                      data-testid={`pub-avail-card-${p.id}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                        <ResponsiveImage
                          src={p.image_url}
                          alt={p.name}
                          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold uppercase tracking-wider shadow-sm">
                          <CheckCircle className="h-3 w-3" />
                          Frei
                        </span>
                      </div>
                      <div className="p-3 md:p-4 flex-1 flex flex-col gap-1.5">
                        <h3 className="text-sm md:text-base font-bold text-sky-700 leading-tight line-clamp-2">
                          {p.name}
                        </h3>
                        {p.price_on_request ? (
                          <p className="text-xs font-bold text-amber-700">Preis auf Anfrage</p>
                        ) : (() => {
                          const unit = effectiveDuration >= 3 ? (p.price_3_days || p.price_1_day) : p.price_1_day;
                          return Number(unit) > 0 ? (
                            <p className="text-xs font-semibold text-neutral-600">
                              <span className="text-sky-700">€{Number(unit).toFixed(0)}</span>
                              <span className="text-neutral-500"> / {effectiveDuration === 1 ? '1 Tag' : `${effectiveDuration} Tage`}</span>
                            </p>
                          ) : null;
                        })()}
                        <span className="mt-auto pt-1.5 inline-flex items-center gap-1 text-xs text-sky-700 font-bold group-hover:text-sky-800">
                          Zum Produkt
                          <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
                {result.unavailable_count > 0 && (
                  <p className="text-center text-xs text-neutral-500 mt-6" data-testid="pub-avail-hint">
                    {result.unavailable_count} weitere Produkte sind an diesem Datum bereits vergeben.
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-neutral-700 font-semibold">
                  An diesem Datum sind keine Produkte verfügbar.
                </p>
                <p className="text-sm text-neutral-500 mt-2">
                  Probiere ein anderes Datum oder kontaktiere uns für individuelle Vorschläge.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicAvailabilitySection;
